import React, { useState, useEffect } from 'react';
import './App.css';
import './RecipeModal.css';
import IngredientSidebar from './IngredientSidebar';
import RecipeSidebar from './RecipeSidebar';
import MenuCanvas from './MenuCanvas';
import RecipeModal from './RecipeModal';
import { DragDropContext } from '@hello-pangea/dnd';
import { findRecipes } from './gemini';
import LandingPage from './LandingPage';

const initialRecipes = [
  { id: '1', name: 'Pancakes', full: 'Full recipe for pancakes...', category: 'breakfast' },
  { id: '2', name: 'Chicken Tikka Masala', full: 'Full recipe for chicken tikka masala...', category: 'lunch' },
  { id: '3', name: 'Spaghetti Bolognese', full: 'Full recipe for spaghetti bolognese...', category: 'dinner' },
  { id: '4', name: 'Trail Mix', full: 'Full recipe for trail mix...', category: 'snacks' },
];

function App() {
  const [showLandingPage, setShowLandingPage] = useState(true);
  const [isFading, setIsFading] = useState(false);
  const [recipes, setRecipes] = useState(initialRecipes);
  const [menu, setMenu] = useState({
    monday: { breakfast: [], lunch: [], dinner: [], snacks: [] },
    tuesday: { breakfast: [], lunch: [], dinner: [], snacks: [] },
    wednesday: { breakfast: [], lunch: [], dinner: [], snacks: [] },
    thursday: { breakfast: [], lunch: [], dinner: [], snacks: [] },
    friday: { breakfast: [], lunch: [], dinner: [], snacks: [] }
  });
  const [selectedRecipe, setSelectedRecipe] = useState(null);
  const [loading, setLoading] = useState(false);
  const [statusMsg, setStatusMsg] = useState('');
  const [apiMessage, setApiMessage] = useState(null);

  const onDragEnd = (result) => {
    const { source, destination, draggableId } = result;

    if (!destination) {
      return;
    }

    const sourceDroppableId = source.droppableId;
    const destinationDroppableId = destination.droppableId;

    if (sourceDroppableId === destinationDroppableId && source.index === destination.index) {
      return;
    }

    const isSourceRecipeList = sourceDroppableId.startsWith('recipes-');
    const isDestRecipeList = destinationDroppableId.startsWith('recipes-');

    // Dragging from the recipe sidebar to a meal slot
    if (isSourceRecipeList && !isDestRecipeList) {
      const [day, meal] = destinationDroppableId.split('-');

      const recipeId = draggableId;
      const recipe = recipes.find(r => r.id === recipeId);

      if (!recipe) {
        return;
      }

      // Add to menu and remove from sidebar
      setMenu(prevMenu => {
        const newMenu = JSON.parse(JSON.stringify(prevMenu));
        newMenu[day][meal].splice(destination.index, 0, recipe);
        return newMenu;
      });
      setRecipes(prevRecipes => prevRecipes.filter(r => r.id !== recipeId));
    }
    // Dragging from a meal slot to another meal slot
    else if (!isSourceRecipeList && !isDestRecipeList) {
      const [sourceDay, sourceMeal] = sourceDroppableId.split('-');
      const [destDay, destMeal] = destinationDroppableId.split('-');

      setMenu(prevMenu => {
        const newMenu = JSON.parse(JSON.stringify(prevMenu)); // Deep copy to avoid mutation
        const sourceList = newMenu[sourceDay][sourceMeal];
        const destList = sourceDay === destDay && sourceMeal === destMeal ? sourceList : newMenu[destDay][destMeal];
        
        const [movedRecipe] = sourceList.splice(source.index, 1);
        destList.splice(destination.index, 0, movedRecipe);

        return newMenu;
      });
    }
    // Dragging from a meal slot back to the recipe sidebar
    else if (!isSourceRecipeList && isDestRecipeList) {
      const [day, meal] = sourceDroppableId.split('-');
      const recipe = menu[day][meal][source.index];

      if (!recipe) {
        return;
      }

      const destCategory = destinationDroppableId.replace('recipes-', '');

      // Remove from menu and add to sidebar
      setMenu(prevMenu => {
        const newMenu = JSON.parse(JSON.stringify(prevMenu));
        newMenu[day][meal].splice(source.index, 1);
        return newMenu;
      });
      setRecipes(prevRecipes => {
        const categoryRecipes = prevRecipes.filter(r => r.category === destCategory);
        const otherRecipes = prevRecipes.filter(r => r.category !== destCategory);
        categoryRecipes.splice(destination.index, 0, { ...recipe, category: destCategory });
        return [...categoryRecipes, ...otherRecipes];
      });
    }
    // Dragging within the recipe sidebar
    else if (isSourceRecipeList && isDestRecipeList) {
      setRecipes(prevRecipes => {
        const sourceCategory = sourceDroppableId.replace('recipes-', '');
        const destCategory = destinationDroppableId.replace('recipes-', '');

        const sourceList = prevRecipes.filter(r => r.category === sourceCategory);
        const destList = sourceCategory === destCategory ? sourceList : prevRecipes.filter(r => r.category === destCategory);
        const otherRecipes = prevRecipes.filter(r => r.category !== sourceCategory && r.category !== destCategory);

        const [moved] = sourceList.splice(source.index, 1);
        moved.category = destCategory;
        destList.splice(destination.index, 0, moved);

        return sourceCategory === destCategory
          ? [...sourceList, ...otherRecipes]
          : [...sourceList, ...destList, ...otherRecipes];
      });
    }
  };

  const handleFindRecipes = async (ingredients) => {
    if (!ingredients.trim()) {
      console.warn('Please enter ingredients');
      return;
    }
    setLoading(true);
    setApiMessage(null);
    try {
      const existingRecipeNames = Object.values(menu)
        .flatMap(day => Object.values(day))
        .flatMap(recipesArray => recipesArray) // flatten the array of arrays
        .filter(recipe => recipe)
        .map(recipe => recipe.name);

      const newRecipes = await findRecipes(ingredients, existingRecipeNames, setStatusMsg);
      if (Array.isArray(newRecipes) && newRecipes.length > 0) {
        // Ensure each recipe has proper id and name
        const validRecipes = newRecipes.map((r, idx) => ({
          id: String(r.id || `recipe-${Date.now()}-${idx}`),
          name: r.name || 'Unknown Recipe',
          full: r.full || 'No recipe details available.',
          category: r.category || 'dinner'
        }));
        setRecipes(validRecipes);
        setApiMessage(null);
      } else if (newRecipes && newRecipes.error) {
        setApiMessage(newRecipes.message);
      }
    } catch (error) {
      console.error('Failed to find recipes:', error);
      setStatusMsg('Failed to find recipes. Please try again.');
    } finally {
      setLoading(false);
      setTimeout(() => setStatusMsg(''), 3000); // Clear the message after 3 seconds
    }
  };

  const handleRecipeClick = (recipe) => {
    setSelectedRecipe(recipe);
  };

  const handleCloseModal = () => {
    setSelectedRecipe(null);
  };

  const handlePrint = () => {
    window.print();
  };

  const handleStartPlanning = () => {
    setIsFading(true);
    setTimeout(() => {
      setShowLandingPage(false);
    }, 500); // Wait for the 500ms fade-out transition to complete
  };

  useEffect(() => {
    let title = 'MealForWeek - Organize Your Meals';
    let description = 'MealForWeek allows you to easily organize your weekly meals, auto-generate recipes via AI, and auto-build grocery lists.';

    if (showLandingPage) {
      title = 'MealForWeek - Weekly Menu Planner & AI Recipes';
      description = "Plan your entire week's meals with intelligent AI. Drag and drop recipes, manage groceries natively, and save hours of prep with MealForWeek.";
    } else {
      title = 'My Weekly Canvas - MealForWeek';
      description = "Your active weekly menu board. Manage your breakfast, lunch, dinner and snacks effortlessly.";
    }

    document.title = title;
    let metaDescription = document.querySelector('meta[name="description"]');
    if (!metaDescription) {
      metaDescription = document.createElement('meta');
      metaDescription.setAttribute('name', 'description');
      document.head.appendChild(metaDescription);
    }
    metaDescription.setAttribute('content', description);

    // Sync SPA state with browser paths for real SEO crawler indexing
    const targetPath = showLandingPage ? '/' : '/planner';
    if (window.location.pathname !== targetPath) {
      window.history.pushState({}, '', targetPath);
    }
  }, [showLandingPage]);

  if (showLandingPage) {
    return <LandingPage onStart={handleStartPlanning} isFading={isFading} />;
  }

  return (
    <DragDropContext onDragEnd={onDragEnd}>
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @media print {
          @page { margin: 1cm; }
          body, html {
            height: auto !important;
            overflow: visible !important;
            background-color: white !important;
          }
          .App {
            height: auto !important;
            overflow: visible !important;
            display: block !important;
            background-color: white !important;
            padding: 0 !important;
          }
          .sidebar {
            display: none !important;
          }
          .menu-canvas {
            overflow: visible !important;
            margin: 0 !important;
            padding: 0 !important;
            box-shadow: none !important;
            background-color: white !important;
          }
          .menu-canvas > div:first-child {
            display: flex !important;
            flex-wrap: nowrap !important;
            gap: 5px !important;
            width: 100% !important;
            justify-content: space-between !important;
          }
          .calendar-day {
            box-shadow: none !important;
            border: 1px solid #bdc3c7 !important;
            flex: 1 1 0 !important;
            min-width: 0 !important;
            padding: 5px !important;
            margin: 0 !important;
            gap: 2px !important;
          }
          .meal-slot {
            border: 1px solid #ecf0f1 !important;
            background-color: #f8f9f9 !important;
            min-height: auto !important;
            page-break-inside: avoid;
            padding: 2px !important;
            margin-top: 2px !important;
            border-radius: 4px !important;
          }
          .meal-slot > div:first-child {
            font-size: 10px !important;
            margin-bottom: 2px !important;
            padding: 0 !important;
          }
          .recipe-card {
            padding: 2px !important;
            margin: 0 !important;
            font-size: 10px !important;
          }
          .calendar-day h3 {
            font-size: 12px !important;
            margin: 2px 0 !important;
            padding: 0 !important;
          }
          /* Hide API Messages from print */
          .menu-canvas > div:nth-child(2) {
            display: none !important;
          }
        }
      `}</style>
      <div className="App" style={{ display: 'flex', height: '100vh', padding: '10px', boxSizing: 'border-box', backgroundColor: '#eaeded', fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif", overflow: 'hidden', animation: 'fadeIn 0.5s ease-in-out' }}>
        <IngredientSidebar onFindRecipes={handleFindRecipes} loading={loading} onPrint={handlePrint} />
        <MenuCanvas menu={menu} apiMessage={apiMessage} />
        <RecipeSidebar recipes={recipes} onRecipeClick={handleRecipeClick} />
        <RecipeModal recipe={selectedRecipe} onClose={handleCloseModal} />
        {statusMsg && (
          <div style={{ position: 'fixed', bottom: '30px', left: '50%', transform: 'translateX(-50%)', backgroundColor: '#2c3e50', color: '#ecf0f1', padding: '12px 24px', borderRadius: '25px', boxShadow: '0 4px 15px rgba(0,0,0,0.2)', zIndex: 1000, fontWeight: 'bold', fontSize: '14px', transition: 'all 0.3s ease' }}>
            {statusMsg}
          </div>
        )}
      </div>
    </DragDropContext>
  );
}

export default App;
